import type { DateTime } from "luxon";
import { getWeekStart } from "@/utils/dateUtils";
import { type ShiftItem, ShiftType, type TransformedShift } from "@/utils/models";

type ShiftsByDate = Record<string, (ShiftItem | TransformedShift)[]>;

export type ShiftWeekGroup = {
	weekStart: DateTime;
	shifts: ShiftsByDate;
	shiftCount: number;
};

/** Label key for a week's group, e.g. "Jun 10 - 16", derived from the (dynamic) week start. */
export const weekKeyOf = (weekStart: DateTime): string => weekStart.toFormat("MMM dd - ") + weekStart.plus({ days: 6 }).toFormat("dd");

/**
 * Order shifts by day, then by start time within the same day so multiple same-day shifts
 * read earliest-first. Entries without a start time (PTO, empty days) sort before timed shifts.
 */
export const compareByDateThenStart = (a: ShiftItem | TransformedShift, b: ShiftItem | TransformedShift): number => {
	const byDate = a.date.diff(b.date).toMillis();
	if (byDate !== 0) return byDate;
	if (!a.startTime || !b.startTime) return Number(!!a.startTime) - Number(!!b.startTime);
	return a.startTime.diff(b.startTime).toMillis();
};

/**
 * Group shifts into weeks anchored on `weekStartDay` (0=Sun..6=Sat). Shared by the standard and
 * medical staff-schedule lists so both honor the dynamic start-of-week identically.
 */
export const groupShiftsByWeek = (shifts: ShiftItem[], weekStartDay: number): Record<string, ShiftWeekGroup> => {
	const grouped = shifts.reduce<Record<string, ShiftWeekGroup>>((acc, shift) => {
		const weekStart = getWeekStart(shift.date, weekStartDay);
		const weekKey = weekKeyOf(weekStart);

		if (!acc[weekKey]) {
			acc[weekKey] = { weekStart, shifts: {}, shiftCount: 0 };
		}
		const dateKey = shift.date.toFormat("yyyy-MM-dd");
		acc[weekKey].shifts[dateKey] = [...(acc[weekKey].shifts[dateKey] ?? []), shift];
		if (shift.shiftType === ShiftType.Day || shift.shiftType === ShiftType.Night || shift.shiftType === ShiftType.All) {
			acc[weekKey].shiftCount++;
		}
		return acc;
	}, {});

	for (const week of Object.values(grouped)) {
		for (const dateKey of Object.keys(week.shifts)) {
			week.shifts[dateKey].sort(compareByDateThenStart);
		}
	}
	return grouped;
};
